/**
 * Single call path from a UI trigger to the `executeWorkflow` callable, holding the
 * run flag. Consumed by `ArWorkflowMenu`, `ArTableControls`, `useFormWorkflowRun`
 * and `RelationshipTree`.
 *
 * Callers that set `sourceTableId` must also pass `resolveRowScope` — the engine
 * rejects a table-scoped run that names no rows rather than reading the whole
 * collection (SYD-2322). The two form-level callers pass neither.
 *
 * `resolveRowScope` runs inside `isRunning`, not before it: it can await a
 * whole-table fetch, and every caller gates its trigger on that flag. Resolving it at
 * the call site left the trigger live for the length of the fetch, and a second click
 * there duplicated the run. A refusal (`null`) makes `runWorkflow` return `null`
 * without calling the engine — the same "did not run" value the callers already
 * short-circuit on, so a refusal can never reach a success message.
 *
 * Each call mints its own `runId` and sends it with the request. The engine keys its run
 * record on that id, so a second invocation carrying the same one replays rather than
 * re-executes; today every click is a new run, and reusing an id across a retry is the
 * follow-up this plumbing enables.
 *
 * `onRunningChange` exists for one caller: `useFormWorkflowRun`'s busy flag is owned
 * by `FormWorkflowActionContext` and shared with `useFormCloseAndCreate` and
 * `useFormStateChange`, so it cannot be replaced by the local `isRunning` without
 * those two losing sight of a workflow run.
 *
 * `stopRun` and `stopPhase` live here rather than in a store because the runner is the
 * only thing that knows both the `runId` and how long its callable stays in flight —
 * `ArTableControls` holds two runners, and a shared leaf would have to tell them apart.
 * The retry loop is here for the same reason: the client holds the `runId` before the
 * server has created the record, so a `not-found` means "not yet" and is worth retrying,
 * but only while this run's own promise is unsettled (SYD-2443).
 */

import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type {
  ExecuteWorkflowResponse,
  Workflow,
  WorkflowRunAttachments,
  WorkflowRowScope,
} from '#types';
import type { ArRowId, ArTableId } from '#types/System.Artifacts.types';
import { executeWorkflow, stopWorkflowRun } from '@/workflows/services/workflowFunctions';
import { readCallableError } from '@/workflows/runtime/readCallableError';
import type { WorkflowRunStopPhase } from '@/workflows/runtime/workflowRunStopPhase.types';

/** Between attempts while the run record does not exist yet — see `stopRun`. */
const STOP_RETRY_MS = 1000;

type UseWorkflowRunnerParams = {
  sourceFormId?: string;
  sourceVersionId?: string;
  sourceTableId?: ArTableId;
  /**
   * Names the rows the run may touch. Required alongside `sourceTableId`; `null` from
   * it refuses the run, having already told the user why.
   */
  resolveRowScope?: (workflow: Workflow) => Promise<WorkflowRowScope | null>;
  /**
   * Mirrors `isRunning` out to a busy flag the caller shares with other hooks. Its
   * identity must be stable across renders: it sits in `runWorkflow`'s dep array, so
   * an inline lambda rebuilds `runWorkflow` on every render and, through
   * `useFormWorkflowRun`, the `FormWorkflowActionContext` value with it — the churn
   * that provider exists to prevent.
   */
  onRunningChange?: (running: boolean) => void;
};

type RunWorkflowParams = {
  workflow: Workflow;
  userParameters: Record<string, unknown>;
  selectedRowIds?: ArRowId[];
  attachments?: WorkflowRunAttachments;
};

export function useWorkflowRunner(params: UseWorkflowRunnerParams) {
  const { sourceFormId, sourceVersionId, sourceTableId, resolveRowScope, onRunningChange } = params;
  const [isRunning, setIsRunning] = useState(false);
  const [stopPhase, setStopPhase] = useState<WorkflowRunStopPhase>('idle');
  /**
   * The run whose callable is in flight. `settled` flips in the run's own `finally`, and
   * `stopRun` reads that object after every await instead of re-reading the ref: a second
   * run started after the first settled is a different object, so a late attempt from the
   * first run's loop can never target it.
   */
  const inFlightRef = useRef<{ runId: string; settled: boolean } | null>(null);

  const runWorkflow = useCallback(
    async ({
      workflow,
      userParameters,
      selectedRowIds,
      attachments,
    }: RunWorkflowParams): Promise<ExecuteWorkflowResponse | null> => {
      if (!workflow.docId || !sourceFormId || !sourceVersionId) {
        return null;
      }

      setIsRunning(true);
      onRunningChange?.(true);
      try {
        const rowScope = resolveRowScope ? await resolveRowScope(workflow) : undefined;
        if (rowScope === null) return null;

        /**
         * `uuidv4`, not `crypto.randomUUID()`: the latter is undefined on an insecure
         * origin, and a developer reaching Vite over a LAN address is on one. The id is
         * held on the in-flight record so `stopRun` can name this run for as long as its
         * callable lasts, and no longer.
         */
        const inFlight = { runId: uuidv4(), settled: false };
        inFlightRef.current = inFlight;
        setStopPhase('stoppable');
        try {
          /**
           * Every optional field is spread conditionally, never spelled. The callable
           * client encodes a property whose value is `undefined` as `null` and only
           * drops a key that is absent, so `sourceTableId,` on a form-level run put
           * `sourceTableId: null` on the wire and the engine read it as an id to
           * validate (SYD-2445).
           */
          return await executeWorkflow({
            runId: inFlight.runId,
            workflowId: workflow.docId,
            sourceFormId,
            sourceVersionId,
            ...(sourceTableId ? { sourceTableId } : {}),
            ...(selectedRowIds ? { selectedRowIds } : {}),
            ...(rowScope ? { rowScope } : {}),
            ...(userParameters ? { userParameters } : {}),
            ...(attachments ? { attachments } : {}),
          });
        } finally {
          inFlight.settled = true;
          inFlightRef.current = null;
          setStopPhase('idle');
        }
      } finally {
        setIsRunning(false);
        onRunningChange?.(false);
      }
    },
    [sourceFormId, sourceVersionId, sourceTableId, resolveRowScope, onRunningChange]
  );

  /**
   * The client holds the `runId` before the server has created the record:
   * `executeWorkflow` loads the workflow, the form, the permission and the send preflight
   * before `beginWorkflowRun`, which can take seconds. A `not-found` in that window is
   * therefore "not yet", and the request is retried every `STOP_RETRY_MS` until it lands
   * or the run's own promise settles — after which there is nothing left to stop.
   * `failed-precondition` is the run finishing first; the run's promise reports that
   * outcome, so this resolves silently. Any other refusal rethrows for the caller to show,
   * and the phase returns to `stoppable` only while the run is still in flight.
   */
  const stopRun = useCallback(async (): Promise<void> => {
    const inFlight = inFlightRef.current;
    if (inFlight === null) return;

    setStopPhase('stopping');
    while (!inFlight.settled) {
      try {
        await stopWorkflowRun({ runId: inFlight.runId });
        return;
      } catch (error) {
        const { code } = readCallableError(error);
        if (code === 'failed-precondition') return;
        if (code !== 'not-found') {
          if (!inFlight.settled) setStopPhase('stoppable');
          throw error;
        }
        await new Promise((resolve) => setTimeout(resolve, STOP_RETRY_MS));
      }
    }
  }, []);

  return {
    isRunning,
    runWorkflow,
    stopRun,
    stopPhase,
  };
}
